import React from 'react';
import { Library, Search, Layers, Link2, BookOpen, Headphones, Video, AudioLines, LineChart, Gauge } from 'lucide-react';
import SEO from '../../components/SEO';
import Section from '../../components/v2/Section';
import V2Hero from '../../components/v2/V2Hero';
import LatestFromBlog from '../../components/v2/LatestFromBlog';
import { PrimaryCTA, CTABlock } from '../../components/v2/CTAButton';
import { HERO } from '../../data/heroImages';
import FeaturedClientBuild from '../../components/v2/FeaturedClientBuild';
import { THRIVE_BUILD } from '../../data/featuredBuilds';
import FAQSchema from '../../components/FAQSchema';
import { DefineSection, FivePartsSection, SeoBenefitsSection, ComparisonSection, ProofStrip, ApproachSection, HubFaqSection, HUB_FAQS } from '../../components/v2/AuthorityHubSections';

const INCLUDES = [
  { icon: Library, t: 'Episode library', d: 'Every podcast episode in one searchable home, listen, read, watch.' },
  { icon: Layers,  t: 'Categories & topics', d: 'Episodes grouped by theme so new visitors find what matters fastest.' },
  { icon: BookOpen, t: 'Start-here path', d: 'A guided journey for first-time visitors that turns browsers into subscribers.' },
  { icon: Link2,   t: 'Internal linking', d: 'Every episode interlinked to related content, for visitors and for Google.' },
  { icon: Headphones, t: 'Multi-format', d: 'Listen, watch, or read, visitors choose. You publish once, we surface it three ways.' },
  { icon: Search,  t: 'Resources & search', d: 'On-site search, downloadables, a guest directory, built for depth, not noise.' },
  { icon: AudioLines, t: 'Voice & AI search schema', d: 'Structured data + speakable schema + transcripts optimised so Siri, Hey Google, ChatGPT and Perplexity can cite you as a source.' },
  { icon: LineChart,  t: 'Google Search Console tracking', d: 'GSC + Bing Webmaster hooked up from day one. Rankings, coverage, backlinks, all watched weekly.' },
  { icon: Gauge,      t: 'Performance monitored', d: 'Core Web Vitals + uptime tracked continuously so your website loads fast and stays online.' },
];

const WHY = [
  { t: 'You own it.', d: 'Social platforms can change rules, throttle reach, or disappear. Your website is on your domain. Forever yours.' },
  { t: 'It’s bingeable.', d: 'Visitors who arrive on episode 47 can find episode 1, 12, and 31 in one click. That’s how trust compounds.' },
  { t: 'It’s searchable, everywhere.', d: 'Not just Google. Your website is optimised to be cited by Siri, Hey Google, Alexa, ChatGPT, Perplexity and Claude. When someone asks "who’s the best X in [your niche]?" your website is the answer.' },
];

export default function AuthorityHub() {
  return (
    <main className="font-body text-[#475569] bg-[#F6F8FB]">
      <SEO
        title="Business Website for Coaches & Authors | Audio & Co."
        description="A business website, or authority hub, that puts your podcast, books, YouTube and blogs in one searchable home for Google and AI. From £495."
        keywords={['business website for coaches', 'website for podcasters uk', 'podcast website', 'author website uk', 'content website', 'website for coaches uk', 'website build for coaches', 'small business website uk', 'content hub website', 'wordpress website for coaches']}
        url="https://audioandco.com/authority-hub"
      />

      <V2Hero image={HERO.cosyDeskNotebook} testId="hub-hero">
        <div className="max-w-3xl">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Business website build · From £495</p>
          <h1 className="font-display font-bold text-[#1E2A44] text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight mt-3">
            Your business website: podcast, books, YouTube, blogs and services in one searchable home.
          </h1>
          <h2 className="font-display font-semibold text-[#1E2A44] text-lg sm:text-xl mt-4 leading-snug">
            A website that indexes your whole business in one place — built to be found on Google, Siri, Hey Google, ChatGPT &amp; Perplexity.
          </h2>
          <p className="text-lg sm:text-xl text-[#475569] mt-6 leading-relaxed">
            Most coaching and author sites hide half the business. Yours will surface every asset you already have —
            podcast episodes, books, YouTube videos, blog articles and service pages — in one owned, searchable
            website. From £495 one-off. The website that finally makes you findable.
          </p>
          <div className="mt-8">
            <CTABlock size="lg" testIdPrefix="hub-hero-cta" />
          </div>
        </div>
      </V2Hero>

      <FeaturedClientBuild build={THRIVE_BUILD} testIdPrefix="hub-featured-build" caseStudyHref="/case-studies/thrive-katie-bell" />

      {/* WHAT IT IS */}
      <Section bg="white" testId="hub-what">
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight">What it is.</h2>
            <p className="text-[#475569] mt-4 leading-relaxed text-lg">
              A proper business website that surfaces everything you already do — your podcast, books, YouTube,
              blog articles and service pages — in one searchable home on your own domain. Not a podcast page
              bolted onto a sales site. Not a link-in-bio. A website built to rank and convert, from £495 one-off.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { icon: Headphones, label: 'Listen' },
              { icon: Video, label: 'Watch' },
              { icon: BookOpen, label: 'Learn' },
            ].map(x => {
              const Icon = x.icon;
              return (
                <div key={x.label} className="bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-6 flex flex-col items-center text-center">
                  <Icon className="w-7 h-7 text-[#BC516F] mb-3" />
                  <div className="font-display font-semibold text-[#1E2A44] text-lg">{x.label}.</div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      <DefineSection />
      <FivePartsSection />

      {/* WHAT IT INCLUDES */}
      <Section bg="bg" testId="hub-includes">
        <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight">What’s inside.</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {INCLUDES.map((x, i) => {
            const Icon = x.icon;
            return (
              <div key={i} className="bg-white border border-[#E2E8F0] rounded-[14px] p-6 sm:p-7 shadow-[0_10px_30px_rgba(30,42,68,0.04)]">
                <div className="w-11 h-11 rounded-lg bg-[#F6F8FB] border border-[#E2E8F0] flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#BC516F]" />
                </div>
                <h3 className="font-display font-semibold text-[#1E2A44] text-lg">{x.t}</h3>
                <p className="text-[#475569] mt-1.5 leading-relaxed text-[15px]">{x.d}</p>
              </div>
            );
          })}
        </div>
      </Section>

      <SeoBenefitsSection />

      {/* WHY IT CONVERTS */}
      <Section bg="white" testId="hub-why">
        <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Why it converts</p>
        <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2 max-w-3xl">An asset that compounds. Not a stream that vanishes.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {WHY.map((w, i) => (
            <div key={i} className="bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-7">
              <h3 className="font-display font-semibold text-[#1E2A44] text-xl">{w.t}</h3>
              <p className="text-[#475569] mt-2 leading-relaxed">{w.d}</p>
            </div>
          ))}
        </div>

        {/* Mid-page CTA banner */}
        <div className="mt-12 rounded-[14px] bg-[#1E2A44] p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-8">
          <div className="flex-1">
            <h3 className="font-display font-bold text-white text-2xl leading-tight">Ready to be the answer when someone asks Siri or ChatGPT?</h3>
            <p className="text-white/75 mt-2 text-sm">Your business website is what makes that possible. Book a 30-min fit call and we&apos;ll show you how it works for your business.</p>
          </div>
          <div className="shrink-0">
            <CTABlock size="lg" invert testIdPrefix="hub-mid-cta" />
          </div>
        </div>
      </Section>

      <ComparisonSection />
      <ProofStrip />
      <ApproachSection />

      {/* PRICING */}
      <Section bg="white" testId="hub-pricing">
        <div className="max-w-3xl">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Pricing</p>
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-3">Business website builds from £495.</h2>
          <p className="text-[#1E2A44] text-2xl font-display font-semibold mt-4">£495 one-off entry price. Scoped per business on the free call.</p>
          <p className="text-[#475569] mt-3 leading-relaxed">
            Every business has a different amount of content to surface — some clients start with just a podcast
            and a services page; others bring years of episodes, three books, a YouTube back catalogue and 60+ blog
            posts. We quote the total after a quick audit on the free call. £495 is where the entry-level site starts;
            bespoke multi-service builds scale from there and are still fixed-fee, not project-creep.
          </p>
          <div className="mt-6">
            <CTABlock size="lg" testIdPrefix="hub-pricing-cta" />
          </div>
        </div>
      </Section>

      {/* LATEST FROM THE BLOG */}
      <LatestFromBlog
        eyebrow="Read next"
        heading="Guides that pair with your business website"
        subheading="How to structure content on your own domain, internal linking, and SEO for the long game."
        bg="bg"
        testId="hub-latest-blog"
      />

      <HubFaqSection />
      <FAQSchema faqs={HUB_FAQS} />

      {/* FINAL CTA */}
      <Section bg="navy" innerClassName="!py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">Want a website like that for your business?</h2>
          <p className="text-white/75 mt-4 text-lg">It’s included in the Pro Plan. We build it. We update it. You record.</p>
          <div className="mt-7 flex justify-center">
            <CTABlock size="lg" align="center" invert testIdPrefix="hub-final-cta" />
          </div>
        </div>
      </Section>
    </main>
  );
}
